/**
 * ManualMarksTemplate component allows the user to edit and update manual marks for a student's submission.
 * The component provides an input field for marks, and buttons to save or discard changes.
 * It also validates the input to ensure it is a valid number and updates the total marks accordingly.
 */

import { useState , useEffect } from "react";
import { InputText } from "primereact/inputtext";
import Button from "react-bootstrap/Button";
import { GiCancel } from "react-icons/gi";
import { TiTick } from "react-icons/ti";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";

const ManualMarksTemplate = ({
  rowData,
  updateSubmission,
  updateTotalMarks,
  disabled = false,
  setSubmissionData = () => {},
  maxMarks = null,
}) => {
  const [editedMarks, setEditedMarks] = useState(
    rowData?.obtained_manual_marks !== undefined
      ? rowData.obtained_manual_marks
      : 0
  );

  const [isDirty, setIsDirty] = useState(false);
  const [newRowData, setNewRowData] = useState(rowData);

  const handleChange = (e) => {
    const value = e.target.value.trim();
    setEditedMarks(value);
    setIsDirty(true);
    setNewRowData((prevRowData) => ({
      ...prevRowData,
      obtained_manual_marks: value,
    }));
  };

  const handleDiscardChanges = () => {
    setEditedMarks(
      rowData.obtained_manual_marks !== undefined
        ? rowData.obtained_manual_marks
        : 0
    );
    setIsDirty(false);
  };

  const handleUpdateSubmission = () => {
    let value = editedMarks.trim();
    if (value === "") {
      setEditedMarks(0);
      value = 0;
    }
    if (Number(value) == value) {
      // maxMarks is 0 when the activity has no evaluation testcases, so there is no ceiling to enforce
      if (maxMarks != null && Number(maxMarks) > 0 && Number(value) > Number(maxMarks)) {
        displayGlobalMessage(
          `Manual marks cannot exceed the evaluation testcase marks (${maxMarks})`,
          "error"
        );
        return;
      }
      rowData.obtained_manual_marks = Number(value);
      updateTotalMarks(
        Number(value) +
          Number(rowData.obtained_hidden_marks) +
          Number(rowData.obtained_rubric_marks)
      );
      updateSubmission(rowData, newRowData.id, value);
      displayGlobalMessage("Manual Marks Updated Successfully","success");
      setIsDirty(false);
      setSubmissionData((prevData) => {
        return prevData.map((submission) =>
          submission.id === newRowData.id
            ? { ...submission, obtained_manual_marks: Number(value) }
            : submission
        );
      });
    } else {
      displayGlobalMessage("Please enter a valid number.", "error");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleUpdateSubmission();
    } else if (e.key === "Escape") {
      handleDiscardChanges();
    }
  };

  useEffect(() => {
    // Reset edited marks and dirty state when rowData changes
    setEditedMarks(
      rowData?.obtained_manual_marks !== undefined
        ? rowData?.obtained_manual_marks
        : 0
    );
    setIsDirty(false);
    setNewRowData(rowData);
  }, [rowData]);

  return (
    <div className="d-flex align-items-center">
      {/* Input field for manual marks */}
      <InputText
        value={editedMarks}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        style={{ width: "80px", height: "30px", color: "black", marginRight: "10px" }}
        disabled={disabled}
      />

      {/* Buttons to save or discard changes, visibility hidden reserves space */}
      <div className="d-flex align-items-center">
        <Button
          variant="success"
          onClick={handleUpdateSubmission}
          className="square-button"
          size="sm"
          style={{ visibility: isDirty ? "visible" : "hidden", marginRight: "5px" }}
        >
          <TiTick />
        </Button>
        <Button
          variant="danger"
          onClick={handleDiscardChanges}
          className="square-button"
          size="sm"
          style={{ visibility: isDirty ? "visible" : "hidden" }}
        >
          <GiCancel />
        </Button>
      </div>
    </div>
  );
};

export default ManualMarksTemplate;
